import { WorkbenchLink } from '@pique/core/modules/workbench';
import clsx from 'clsx';
import React from 'react';
import type { To } from 'react-router-dom';

import * as styles from './index.css';

export interface MenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: React.ReactElement;
  active?: boolean;
  disabled?: boolean;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  postfix?: React.ReactElement;
}

export interface MenuLinkItemProps extends MenuItemProps {
  to: To;
  linkComponent?: React.ComponentType<
    React.PropsWithChildren<{ to: To; className?: string }>
  >;
}

export const MenuItem = ({
  ref,
  onClick,
  icon,
  active,
  children,
  disabled,
  collapsed,
  onCollapsedChange,
  postfix,
  ...props
}: MenuItemProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const collapsible = onCollapsedChange !== undefined;

  const handleClick = collapsible
    ? (e: React.MouseEvent<HTMLDivElement>) => {
        onCollapsedChange?.(!collapsed);
        onClick?.(e);
      }
    : onClick;

  return (
    <div
      ref={ref}
      {...props}
      onClick={handleClick}
      className={clsx([styles.root, props.className])}
      data-active={active}
      data-disabled={disabled}
      data-collapsible={collapsible}
    >
      {icon && (
        <div className={styles.iconsContainer}>
          {React.cloneElement<{ className?: string }>(
            icon as React.ReactElement<{ className?: string }>,
            {
              className: clsx([
                styles.icon,
                (icon as React.ReactElement<{ className?: string }>).props
                  .className,
              ]),
            }
          )}
        </div>
      )}

      <div className={styles.content}>{children}</div>
      {postfix ? (
        <div className={styles.postfix}>
          {postfix}
        </div>
      ) : null}
    </div>
  );
};

export const MenuLinkItem = ({
  ref,
  to,
  linkComponent: LinkComponent = WorkbenchLink,
  ...props
}: MenuLinkItemProps & { ref?: React.Ref<HTMLDivElement> }) => {
  return (
    <LinkComponent to={to} className={styles.linkItemRoot}>
      <MenuItem ref={ref} {...props}></MenuItem>
    </LinkComponent>
  );
};
